import React, { useState, useEffect } from "react";
import axios from "axios";
import { Row, Col, Card, ProgressBar, Spinner, Button, Badge, Table } from "react-bootstrap";

import { CONFIG } from "../utils/config";
import { KpiCard } from "../components/CommonUI";

function DashboardView({ onDrillDown }) {
  // Enhanced State for deeper analytics
  const [metrics, setMetrics] = useState({
    total: 0,
    success: 0,
    fail: 0,
    cancelled: 0,
    rate: 0,
    singleCount: 0,
    groupCount: 0,
    bulkCount: 0
  });

  const [recentLogs, setRecentLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  
  // NEW: State for export loading
  const [isExporting, setIsExporting] = useState(false);

  const fetchData = () => {
    setLoading(true);
    axios.get(`${CONFIG.DB_BASE}/history`)
      .then(res => {
        const data = res.data || [];

        // 1. Basic KPIs
        const total = data.length;
        
        // 2. logic update: Explicitly filter statuses
        const success = data.filter(d => d.status === 'Success').length;
        const cancelled = data.filter(d => d.status === 'Cancelled').length; 
        
        // Assuming 'Failed' or 'Error' counts as a failure
        const fail = data.filter(d => d.status === 'Failed' || d.status === 'Error').length;
        
        const rate = total > 0 ? ((success / total) * 100).toFixed(1) : 0;

        // 3. Traffic Distribution Analysis (Single vs Bulk)
        const singleCount = data.filter(d =>
          d.operationType.toLowerCase().includes("single") &&
          !d.operationType.toLowerCase().includes("group") &&
          !d.operationType.toLowerCase().includes("bulk")
        ).length;

        const groupCount = data.filter(d =>
          d.operationType.toLowerCase().includes("group")
        ).length;

        const bulkCount = data.filter(d =>
          d.operationType.toLowerCase().includes("bulk")
        ).length;

        // 4. Recent Activity Feed (Sort desc by timestamp, take top 5)
        const sortedData = [...data].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        const recent = sortedData.slice(0, 5);

        setMetrics({ total, success, fail, cancelled, rate, singleCount, groupCount, bulkCount });

        setRecentLogs(recent);
        setLastUpdated(new Date());
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

// Function to handle Advanced Excel Export
  const handleExportReport = async () => {
    try {
      setIsExporting(true);

      // 1. Request the file as a 'blob' (binary data)
      const response = await axios.get(`${CONFIG.DB_BASE}/export-audit-advanced`, {
        responseType: 'blob', // <--- CRITICAL: specificies this is a file, not JSON
      });

      // 2. Create the URL for the file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      
      // 3. Create a temporary link element
      const link = document.createElement('a');
      link.href = url;
      
      // 4. FORCE the filename with .xlsx extension
      // We use the current date to make the filename unique
      const dateStr = new Date().toISOString().slice(0,10).replace(/-/g, ""); // e.g. 20231025
      const fileName = `Audit_Report_${dateStr}.xlsx`; 
      
      link.setAttribute('download', fileName); // <--- This forces the extension
      
      // 5. Trigger the download
      document.body.appendChild(link);
      link.click();
      
      // 6. Cleanup
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error("Export failed:", error);
      alert("Failed to download the report.");
    } finally {
      setIsExporting(false);
    }
  };
  // Calculate dynamic quota usage simulation (e.g., 20000 limit)
  const quotaLimit = 20000;
  const quotaUsedPercent = Math.min((metrics.total / quotaLimit) * 100, 100).toFixed(1);

  // Traffic Distribution visibility logic
  const total = metrics.total || 1;
  const singlePct = (metrics.singleCount / total) * 100;
  const groupPct  = (metrics.groupCount / total) * 100;
  const bulkPct   = (metrics.bulkCount / total) * 100;

  const minPct = 6;
  const displaySingle = singlePct > 0 ? Math.max(singlePct, minPct) : 0;
  const displayGroup  = groupPct > 0  ? Math.max(groupPct,  minPct) : 0;
  const displayBulk   = bulkPct > 0   ? Math.max(bulkPct,   minPct) : 0;

  if (loading && metrics.total === 0) return <div className="p-5 text-center"><Spinner animation="grow" variant="primary" /> <p className="mt-3 text-muted">Loading Real-time Analytics...</p></div>;
  
  return (
    <div className="fade-in">
      {/* Header & Controls */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold text-secondary m-0">System Overview</h4>
          <small className="text-muted">
            <i className="bi bi-clock-history me-1"></i>
            Data as of: {lastUpdated.toLocaleTimeString()}
          </small>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-primary" size="sm" onClick={fetchData}>
            <i className="bi bi-arrow-clockwise me-1"></i> Refresh
          </Button>
          <Badge bg="success" pill className="px-3 py-2 d-flex align-items-center">
            <i className="bi bi-wifi me-2"></i> Online
          </Badge>
        </div>
      </div>

      {/* Row 1: KPI Cards - Updated to 5 Columns using row-cols classes */}
      <Row className="g-3 mb-4 row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-xl-5">
        <Col>
          <KpiCard title="Total Operations" value={metrics.total} icon="bi-hdd-stack" bg="primary" onClick={() => onDrillDown("history", "All")} />
        </Col>
        <Col>
          <KpiCard title="Messages Sent" value={metrics.success} icon="bi-check-circle" bg="success" onClick={() => onDrillDown("history", "Success")} />
        </Col>
        
        {/* 1. Failed Card (Specific) */}
        <Col>
          <KpiCard 
            title="Failed" 
            value={metrics.fail} 
            icon="bi-x-octagon" 
            bg="danger" 
            onClick={() => onDrillDown("history", "Failed")} 
          />
        </Col>

        {/* 2. New Cancelled Card */}
        <Col>
          <KpiCard 
            title="Cancelled" 
            value={metrics.cancelled} 
            icon="bi-slash-circle" 
            bg="warning"
            onClick={() => onDrillDown("history", "Cancelled")} 
          />
        </Col>

        <Col>
          <KpiCard title="Success Rate" value={`${metrics.rate}%`} icon="bi-graph-up-arrow" bg="info" onClick={() => onDrillDown("history", "All")} />
        </Col>
      </Row>

      {/* Row 2: Deep Analytics & Visualization */}
      <Row className="mb-4">
        {/* Traffic Distribution & Recent Logs */}
        <Col lg={8} md={12} className="mb-4 mb-lg-0">
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white fw-bold border-bottom-0 pt-3">
              <i className="bi bi-bar-chart-fill me-2 text-primary"></i> Traffic Distribution
            </Card.Header>
            <Card.Body>
              <div className="mb-4">
                <div className="d-flex justify-content-between mb-1 small fw-bold">
                  <span>Message Type Split</span>
                  <span>{metrics.total} Total</span>
                </div>
                {/* Visual Bar Chart using Stacked Progress */}
                <ProgressBar style={{ height: "30px" }} className="shadow-sm">
                  <ProgressBar
                    variant="primary"
                    now={displaySingle}
                    label={`${metrics.singleCount} Single`}
                    key={1}
                  />
                  <ProgressBar
                    variant="info"
                    now={displayGroup}
                    label={`${metrics.groupCount} Group`}
                    key={2}
                  />
                  <ProgressBar
                    variant="warning"
                    now={displayBulk}
                    label={`${metrics.bulkCount} Bulk`}
                    key={3}
                  />
                </ProgressBar>

                <div className="d-flex justify-content-between mt-2 text-muted small">
                  <span><i className="bi bi-circle-fill text-primary me-1"></i>Single SMS</span>
                  <span><i className="bi bi-circle-fill text-info me-1"></i>Group Batches</span>
                  <span><i className="bi bi-circle-fill text-warning me-1"></i>Bulk Uploads</span>
                </div>
              </div>

              <h6 className="fw-bold text-muted border-bottom pb-2 mb-3">Recent Activity Feed</h6>
              <div className="table-responsive">
                <Table hover size="sm" className="align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Time</th>
                      <th>Type</th>
                      <th>Target</th>
                      <th className="text-end">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentLogs.length > 0 ? recentLogs.map(log => (
                      <tr key={log.id} style={{ cursor: 'pointer' }} onClick={() => onDrillDown("history", "All")}>
                        <td className="small text-muted">{new Date(log.timestamp).toLocaleTimeString()}</td>
                        <td><Badge bg="light" text="dark" className="border">{log.operationType}</Badge></td>
                        <td className="small font-monospace text-truncate" style={{ maxWidth: "150px" }} title={log.target}>{log.target}</td>
                        <td className="text-end">
                          {/* Updated Status Badge Logic to handle Cancelled */}
                          <Badge bg={
                              log.status === 'Success' ? 'success' : 
                              log.status === 'Cancelled' ? 'warning' : 'danger'
                            } pill>
                            {log.status === 'Success' && <i className="bi bi-check"></i>}
                            {log.status === 'Cancelled' && <i className="bi bi-slash-circle"></i>}
                            {(log.status === 'Failed' || log.status === 'Error') && <i className="bi bi-x"></i>}
                          </Badge>
                        </td>
                      </tr>
                    )) : (
                      <tr><td colSpan="4" className="text-center text-muted small py-3">No recent activity found.</td></tr>
                    )}
                  </tbody>
                </Table>
              </div>
              <div className="text-center mt-3">
                <Button variant="link" size="sm" onClick={() => onDrillDown("history", "All")}>View All History <i className="bi bi-arrow-right"></i></Button>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* System Health & Resources */}
        <Col lg={4} md={12}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white fw-bold border-bottom-0 pt-3">
              <i className="bi bi-cpu-fill me-2 text-danger"></i> System Health
            </Card.Header>
            <Card.Body>
              <div className="mb-4">
                <div className="d-flex justify-content-between mb-1">
                  <span className="small fw-bold">API Latency</span>
                  <span className="fw-bold text-primary small">~24ms</span>
                </div>
                <ProgressBar now={15} variant="primary" style={{ height: "6px" }} />
              </div>

              <div className="mb-4">
                <div className="d-flex justify-content-between mb-1">
                  <span className="small fw-bold">Daily Quota ({quotaLimit})</span>
                  <span className={`fw-bold small ${quotaUsedPercent > 80 ? 'text-danger' : 'text-success'}`}>{quotaUsedPercent}% Used</span>
                </div>
                <ProgressBar now={quotaUsedPercent} variant={quotaUsedPercent > 80 ? "danger" : "success"} style={{ height: "6px" }} />
              </div>

              <div className="mb-4">
                <div className="d-flex justify-content-between mb-1">
                  <span className="small fw-bold">Database Health</span>
                  <span className="fw-bold text-info small">Optimal</span>
                </div>
                <ProgressBar now={98} variant="info" style={{ height: "6px" }} />
              </div>

              {/* <div className="mt-5 p-3 bg-light rounded border">
                <h6 className="fw-bold text-secondary mb-2">Export Tools</h6>
                <p className="small text-muted mb-3">Download full audit logs for compliance.</p>
                <div className="d-grid gap-2">
                  <Button 
                    variant="outline-dark" 
                    size="sm" 
                    onClick={handleExportReport} 
                    disabled={isExporting}
                  >
                    {isExporting ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Generating...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-file-earmark-spreadsheet me-2"></i> Download Excel Report
                      </>
                    )}
                  </Button>
                </div>
              </div> */}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default DashboardView;